import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "prompts";

const EMPTY = { shown: 0, dismissed: 0, clicked: false };

// Estado de los avisos que se muestran un número acotado de veces, en una sola
// clave de localStorage indexada por prompt:
//   { "app-download": { shown: 2, dismissed: 1, clicked: true } }
//
// Los suscriptores viven en el módulo y no en el hook, igual que el índice de
// useCrontuLinks: con una copia por instancia, escribir el estado de un prompt
// pisaría el de otro.
//
// Ojo: `useUser().actions.logout()` hace `localStorage.clear()`, así que al
// cerrar sesión los contadores vuelven a cero y los avisos reaparecen. Decisión
// tomada y aceptada (card 687) — el público es browser móvil, o sea teléfono
// personal, donde localStorage es efectivamente por usuario.
const listeners = new Set();

const read = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const notify = () => {
  const all = read();
  listeners.forEach((listener) => listener(all));
};

// Se relee localStorage antes de escribir para no pisar lo que haya guardado
// otra pestaña desde la última lectura. Devuelve el estado ya actualizado
// porque los eventos de GA4 mandan el valor nuevo, no el anterior.
//
// Devuelve `null` si la escritura no entró — storage deshabilitado o cuota
// llena. Es importante que el caller se entere: un aviso con tope de
// apariciones que no puede registrar la suya tiene el tope inaplicable, y
// devolviéndole el valor nuevo igual creería que quedó guardado.
const update = (key, change) => {
  const all = read();
  const current = { ...EMPTY, ...all[key] };
  const next = { ...current, ...change(current) };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...all, [key]: next }));
  } catch {
    return null;
  }
  notify();
  return next;
};

const bumpShown = (key) => update(key, (c) => ({ shown: c.shown + 1 }));
const bumpDismissed = (key) => update(key, (c) => ({ dismissed: c.dismissed + 1 }));
const setClicked = (key) => update(key, () => ({ clicked: true }));

// Otra pestaña tocó un aviso: se adopta su versión en vez de seguir con la
// propia. `key === null` es un localStorage.clear(), o sea el logout.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    notify();
  });
}

const usePromptState = (key) => {
  const [prompts, setPrompts] = useState(read);

  useEffect(() => {
    listeners.add(setPrompts);
    // Otra instancia pudo haber escrito entre el primer render y este effect.
    setPrompts(read());
    return () => listeners.delete(setPrompts);
  }, []);

  return {
    ...EMPTY,
    ...prompts[key],
    markShown: useCallback(() => bumpShown(key), [key]),
    markDismissed: useCallback(() => bumpDismissed(key), [key]),
    markClicked: useCallback(() => setClicked(key), [key]),
  };
};

export default usePromptState;
